/**
 * External dependencies
 */
import styled from 'styled-components';
import { __ } from '@web-stories-wp/i18n';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { SearchInput } from '../../common';
import LoadingPill from '../common/loadingPill';
import {
  MediaGalleryMessage,
  PaneHeader,
  PaneInner,
  SearchInputContainer,
  StyledPane,
} from '../media/common/styles';
import { PANE_PADDING } from '../shared';
import LottieCard from './lottieCard';
import paneId from './paneId';
import useInsertLottieAnimation from './useInsertLottieAnimation';
import useLottieLibrary from './useLottieLibrary';

const PaneContent = styled.div`
  position: relative;
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0 ${PANE_PADDING} 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0 0;
`;

const MessageStack = styled.div`
  padding-top: 16px;
`;

function LottiePane(props) {
  const {
    animations,
    error,
    hasMore,
    isLoaded,
    isLoading,
    loadMore,
    retry,
    search,
    searchTerm,
  } = useLottieLibrary();
  const { insertLottieAnimation, pendingAnimationIds } =
    useInsertLottieAnimation();
  const pendingAnimationIdsSet = new Set(pendingAnimationIds);
  const isSearching = searchTerm.length > 0;
  const showInitialLoading = isLoading && !isLoaded;
  const showEmpty = isLoaded && !isLoading && !error && animations.length === 0;

  return (
    <StyledPane id={paneId} {...props}>
      <PaneInner>
        <PaneHeader>
          <SearchInputContainer marginTop={12}>
            <SearchInput
              incremental
              initialValue={searchTerm}
              onSearch={search}
              placeholder={__('Search', 'web-stories')}
            />
          </SearchInputContainer>
        </PaneHeader>
        <PaneContent>
          {error ? (
            <MessageStack>
              <MediaGalleryMessage>
                {__('Animations could not be loaded.', 'web-stories')}
              </MediaGalleryMessage>
              <Actions>
                <Button
                  variant={BUTTON_VARIANTS.RECTANGLE}
                  type={BUTTON_TYPES.SECONDARY}
                  size={BUTTON_SIZES.SMALL}
                  onClick={retry}
                >
                  {__('Retry', 'web-stories')}
                </Button>
              </Actions>
            </MessageStack>
          ) : showEmpty ? (
            <MediaGalleryMessage>
              {isSearching
                ? __('No results found.', 'web-stories')
                : __('No animations found.', 'web-stories')}
            </MediaGalleryMessage>
          ) : (
            <>
              <Grid
                role="list"
                aria-label={__('Lottie animations', 'web-stories')}
              >
                {animations.map((animation) => (
                  <LottieCard
                    key={animation.id}
                    animation={animation}
                    isPending={pendingAnimationIdsSet.has(animation.id)}
                    onInsert={insertLottieAnimation}
                  />
                ))}
              </Grid>
              {hasMore && (
                <Actions>
                  <Button
                    variant={BUTTON_VARIANTS.RECTANGLE}
                    type={BUTTON_TYPES.SECONDARY}
                    size={BUTTON_SIZES.SMALL}
                    disabled={isLoading}
                    onClick={loadMore}
                  >
                    {__('Load more', 'web-stories')}
                  </Button>
                </Actions>
              )}
            </>
          )}
          {(showInitialLoading || (isLoading && isLoaded)) && <LoadingPill />}
        </PaneContent>
      </PaneInner>
    </StyledPane>
  );
}

export default LottiePane;
